/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
package analytics

import kotlin.math.*

/**
 * Generates personalized budget improvement recommendations based on spending analysis
 */
class RecommendationEngine {
    
    /**
     * Generate recommendations based on trend analysis, anomalies, and patterns
     */
    fun generateRecommendations(
        trendDirection: TrendDirection,
        anomalies: List<Anomaly>,
        patterns: SpendingPatterns,
        averageMonthlySpending: Double,
        highestCategory: String?
    ): List<Recommendation> {
        
        if (trendDirection.direction == "INSUFFICIENT_DATA" || averageMonthlySpending <= 0) {
            return listOf(
                Recommendation(
                    type = "INSUFFICIENT_DATA",
                    priority = "LOW",
                    message = "Need more spending data to generate personalized recommendations. Continue tracking for better insights.",
                    category = null,
                    actionItems = listOf("Continue using the app for at least 30 days"),
                    timeframe = "1 month",
                    savingsAmount = 0.0
                )
            )
        }
        
        val recommendations = mutableListOf<Recommendation>()
        
        // Trend-based recommendations
        recommendations.addAll(generateTrendRecommendations(trendDirection, averageMonthlySpending, highestCategory))
        
        // Anomaly-based recommendations
        recommendations.addAll(generateAnomalyRecommendations(anomalies))
        
        // Pattern-based recommendations
        recommendations.addAll(generatePatternRecommendations(patterns))
        
        // Volatility-based recommendations
        if (trendDirection.volatility > 100.0) {
            recommendations.add(generateVolatilityRecommendation(trendDirection.volatility))
        }
        
        // Category-specific recommendations
        highestCategory?.let { category ->
            recommendations.add(generateCategoryRecommendation(category, averageMonthlySpending))
        }
        
        // Sort by priority and return top recommendations
        return recommendations
            .distinctBy { it.type + it.category }
            .sortedWith(compareBy<Recommendation> { 
                when (it.priority) {
                    "HIGH" -> 0
                    "MEDIUM" -> 1
                    else -> 2
                }
            }.thenByDescending { it.savingsAmount })
            .take(5) // Limit to top 5 recommendations
    }
    
    /**
     * Generate specific recommendations for achieving a budget goal
     */
    fun generateBudgetGoalRecommendations(
        currentMonthlyAverage: Double,
        targetMonthlyBudget: Double,
        highestCategories: List<Pair<String, Double>>
    ): List<Recommendation> {
        
        val neededReduction = currentMonthlyAverage - targetMonthlyBudget
        if (neededReduction <= 0) {
            return listOf(
                Recommendation(
                    type = "BUDGET_MET",
                    priority = "LOW",
                    message = "Great job! You're already within your target budget.",
                    category = null,
                    actionItems = listOf("Maintain current spending habits", "Consider saving the extra amount"),
                    timeframe = "ongoing",
                    savingsAmount = abs(neededReduction)
                )
            )
        }
        
        val recommendations = mutableListOf<Recommendation>()
        var remainingReduction = neededReduction
        
        // Target highest spending categories first
        highestCategories.take(3).forEach { (category, amount) ->
            if (remainingReduction > 0) {
                val targetReduction = min(remainingReduction, amount * 0.3) // Reduce by up to 30%
                
                recommendations.add(
                    Recommendation(
                        type = "BUDGET_GOAL_REDUCTION",
                        priority = if (targetReduction > neededReduction * 0.3) "HIGH" else "MEDIUM",
                        message = "Reduce $category spending by $${targetReduction.toInt()} to reach your budget goal.",
                        category = category,
                        actionItems = getCategoryActionItems(category, targetReduction),
                        timeframe = "30 days",
                        savingsAmount = targetReduction
                    )
                )
                
                remainingReduction -= targetReduction
            }
        }
        
        return recommendations
    }
    
    private fun generateTrendRecommendations(
        trend: TrendDirection,
        averageSpending: Double,
        highestCategory: String?
    ): List<Recommendation> {
        
        return when (trend.direction) {
            "INCREASING" -> {
                val urgency = when {
                    trend.slope > 100 -> "HIGH"
                    trend.slope > 50 -> "MEDIUM" 
                    else -> "LOW"
                }
                
                listOf(
                    Recommendation(
                        type = if (urgency == "HIGH") "URGENT_ACTION" else "REDUCE_SPENDING",
                        priority = urgency,
                        message = if (urgency == "HIGH") {
                            "Your spending is increasing rapidly by $${trend.slope.toInt()}/month. Take immediate action to avoid budget overrun."
                        } else {
                            "Your spending trend is increasing by $${trend.slope.toInt()}/month. Consider reviewing your budget."
                        },
                        category = highestCategory,
                        actionItems = listOf(
                            "Review recent transactions for unnecessary expenses",
                            "Set up spending alerts for your highest category",
                            "Consider using a weekly spending limit"
                        ),
                        timeframe = if (urgency == "HIGH") "1 week" else "2 weeks",
                        savingsAmount = trend.slope * 2 // Potential 2-month savings
                    )
                )
            }
            
            "DECREASING" -> {
                listOf(
                    Recommendation(
                        type = "POSITIVE_FEEDBACK",
                        priority = "LOW",
                        message = "Excellent! Your spending is decreasing by $${abs(trend.slope).toInt()}/month. Keep up the good work!",
                        category = null,
                        actionItems = listOf(
                            "Continue your current spending habits",
                            "Consider saving the reduced amount",
                            "Set up automatic savings for the difference"
                        ),
                        timeframe = "ongoing",
                        savingsAmount = abs(trend.slope)
                    )
                )
            }
            
            "STABLE" -> {
                if (trend.confidence > 0.8) {
                    listOf(
                        Recommendation(
                            type = "OPTIMIZE_STABLE",
                            priority = "LOW",
                            message = "Your spending is stable. Look for optimization opportunities to maximize savings.",
                            category = highestCategory,
                            actionItems = listOf(
                                "Review subscription services for unused accounts",
                                "Look for better deals on regular purchases",
                                "Consider cashback or rewards programs"
                            ),
                            timeframe = "1 month",
                            savingsAmount = averageSpending * 0.05 // 5% optimization target
                        )
                    )
                } else emptyList()
            }
            
            else -> emptyList()
        }
    }
    
    private fun generateAnomalyRecommendations(anomalies: List<Anomaly>): List<Recommendation> {
        val recommendations = mutableListOf<Recommendation>()
        
        val highSpendingAnomalies = anomalies.filter { it.type == "HIGH_SPENDING" }
        if (highSpendingAnomalies.isNotEmpty()) {
            val totalExcess = highSpendingAnomalies.sumOf { it.value - it.expectedValue }
            
            recommendations.add(
                Recommendation(
                    type = "SAVINGS_OPPORTUNITY",
                    priority = "MEDIUM",
                    message = "You had ${highSpendingAnomalies.size} high spending incidents. " +
                            "Avoiding similar overspending could save $${totalExcess.toInt()} per month.",
                    category = null,
                    actionItems = listOf(
                        "Identify triggers for high spending days",
                        "Set up spending notifications",
                        "Use the 24-hour rule before large purchases"
                    ),
                    timeframe = "2 weeks",
                    savingsAmount = totalExcess
                )
            )
        }
        
        return recommendations
    }
    
    private fun generatePatternRecommendations(patterns: SpendingPatterns): List<Recommendation> {
        val recommendations = mutableListOf<Recommendation>()
        
        patterns.seasonalPattern?.let { seasonal ->
            if (seasonal.confidence > 0.7) {
                recommendations.add(
                    Recommendation(
                        type = "SEASONAL_ADJUSTMENT",
                        priority = "MEDIUM",
                        message = "Your spending follows a ${seasonal.period}-period cycle. " +
                                "Plan ahead for high spending periods to maintain your budget.",
                        category = null,
                        actionItems = listOf(
                            "Create separate budgets for high and low spending periods",
                            "Save extra during low spending periods",
                            "Set reminders before predictable high spending periods"
                        ),
                        timeframe = "next ${seasonal.period} periods",
                        savingsAmount = 0.0
                    )
                )
            }
        }
        
        patterns.trendChange?.let { change ->
            if (change.magnitude > 50.0) {
                recommendations.add(
                    Recommendation(
                        type = "TREND_ADJUSTMENT",
                        priority = "MEDIUM",
                        message = "Your spending changed significantly on period ${change.changePoint}. " +
                                "Review what caused this ${change.direction.lowercase()} trend.",
                        category = null,
                        actionItems = listOf(
                            "Review transactions around the change point",
                            "Identify if the change was intentional or circumstantial",
                            "Adjust future budgets based on the new pattern"
                        ),
                        timeframe = "1 week",
                        savingsAmount = if (change.direction == "UP") change.magnitude else 0.0
                    )
                )
            }
        }
        
        return recommendations
    }
    
    private fun generateVolatilityRecommendation(volatility: Double): Recommendation {
        return Recommendation(
            type = "STABILIZE_SPENDING",
            priority = "MEDIUM",
            message = "Your spending varies significantly from month to month. " +
                    "More consistent spending will help with budget planning.",
            category = null,
            actionItems = listOf(
                "Create a weekly spending plan",
                "Use consistent shopping days",
                "Set up automatic payments for regular expenses",
                "Track daily spending to catch deviations early"
            ),
            timeframe = "4 weeks",
            savingsAmount = volatility * 0.3 // Potential savings from stabilization
        )
    }
    
    private fun generateCategoryRecommendation(category: String, averageSpending: Double): Recommendation {
        val categoryAdvice = getCategoryAdvice(category)
        val potentialSavings = averageSpending * 0.1 // 10% savings target
        
        return Recommendation(
            type = "CATEGORY_OPTIMIZATION",
            priority = "MEDIUM",
            message = "$category is your highest spending category. ${categoryAdvice.tip}",
            category = category,
            actionItems = categoryAdvice.actions,
            timeframe = "2 weeks",
            savingsAmount = potentialSavings
        )
    }
    
    private fun getCategoryActionItems(category: String, targetReduction: Double): List<String> {
        return when (category.lowercase()) {
            "dining", "food", "restaurants" -> listOf(
                "Plan meals in advance",
                "Cook more meals at home",
                "Limit restaurant visits to ${((targetReduction / 25).toInt().coerceAtLeast(1))} times per week"
            )
            "shopping", "retail" -> listOf(
                "Create shopping lists and stick to them",
                "Wait 24 hours before non-essential purchases",
                "Unsubscribe from promotional emails"
            )
            "entertainment" -> listOf(
                "Look for free or low-cost entertainment options",
                "Take advantage of happy hours and discounts",
                "Limit subscription services"
            )
            "groceries" -> listOf(
                "Use grocery lists and meal planning",
                "Buy generic brands where possible",
                "Shop sales and use coupons"
            )
            else -> listOf(
                "Track all expenses in this category",
                "Look for alternatives or better deals",
                "Set a specific budget limit"
            )
        }
    }
    
    private fun getCategoryAdvice(category: String): CategoryAdvice {
        return when (category.lowercase()) {
            "dining", "food", "restaurants" -> CategoryAdvice(
                tip = "Try meal prepping on weekends to reduce restaurant spending.",
                actions = listOf(
                    "Plan weekly meals in advance",
                    "Cook larger portions for leftovers", 
                    "Limit dining out to special occasions"
                )
            )
            "shopping", "retail" -> CategoryAdvice(
                tip = "Use the 48-hour rule for non-essential purchases.",
                actions = listOf(
                    "Unsubscribe from store newsletters",
                    "Shop with a specific list",
                    "Compare prices before buying"
                )
            )
            "entertainment" -> CategoryAdvice(
                tip = "Look for free events and activities in your area.",
                actions = listOf(
                    "Check local community calendars",
                    "Use library resources for books and movies",
                    "Find free outdoor activities"
                )
            )
            "transportation" -> CategoryAdvice(
                tip = "Consider carpooling or public transport to reduce costs.",
                actions = listOf(
                    "Combine errands into single trips",
                    "Walk or bike for short distances",
                    "Use ride-sharing for occasional trips"
                )
            )
            else -> CategoryAdvice(
                tip = "Look for ways to optimize spending in this category.",
                actions = listOf(
                    "Track all expenses carefully",
                    "Research alternatives or discounts",
                    "Set specific spending limits"
                )
            )
        }
    }
}

/**
 * Represents a budget improvement recommendation
 */
data class Recommendation(
    val type: String, // Type of recommendation (e.g., "REDUCE_SPENDING", "POSITIVE_FEEDBACK")
    val priority: String, // "HIGH", "MEDIUM", "LOW"
    val message: String, // Human-readable recommendation message
    val category: String?, // Spending category this applies to (if any)
    val actionItems: List<String>, // Specific actionable steps
    val timeframe: String, // When to implement (e.g., "1 week", "30 days")
    val savingsAmount: Double // Estimated monthly savings potential
)

/**
 * Category-specific advice
 */
private data class CategoryAdvice(
    val tip: String,
    val actions: List<String>
)